import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toastError, toastSuccess } from "helpers";
import { createLogger } from "logging";
import { useApi, useBoardSettings, useTranslations } from "providers";
import React, { PropsWithChildren, useCallback, useMemo, useState } from "react";

import { BoardSendToContext } from "./BoardSendToContext";
import { BoardSendToPanel } from "./components/BoardSendToPanel";

const logger = createLogger("BoardSendToProvider");

export const BoardSendToProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const i18n = useTranslations();
  const { boardSendToApi } = useApi();
  const { boardId, reloadBoardSettings } = useBoardSettings();
  const queryClient = useQueryClient();

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const queryKey = useMemo(() => [`BoardSendTo|${boardId}`], [boardId]);
  const queryResult = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await boardSendToApi.getTargets(boardId!);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: !!boardId,
    staleTime: 0,
  });
  const isLoading = queryResult.isPending;
  const results = queryResult.data;

  const invalidate = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  const addOutgoingMutation = useMutation({
    mutationFn: (targetId: number) => {
      return boardSendToApi.addOutgoing(boardId!, targetId);
    },
    onSuccess: () => {
      toastSuccess(i18n.SendTo_Add_Success);
      invalidate();
    },
    onError: (error: unknown) => toastError(i18n.SendTo_Add_Error, error),
  });

  const addIncomingMutation = useMutation({
    mutationFn: (sourceId: number) => {
      return boardSendToApi.addIncoming(boardId!, sourceId);
    },
    onSuccess: () => {
      toastSuccess(i18n.SendTo_Add_Success);
      invalidate();
    },
    onError: (error: unknown) => toastError(i18n.SendTo_Add_Error, error),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => {
      return boardSendToApi.remove(boardId!, id);
    },
    onSuccess: () => {
      toastSuccess(i18n.SendTo_Remove_Success);
      invalidate();
    },
    onError: (error: unknown) => toastError(i18n.SendTo_Remove_Error, error),
  });

  const addOutgoing = useCallback(
    async (targetId: number) => {
      await addOutgoingMutation.mutateAsync(targetId);
    },
    [addOutgoingMutation]
  );

  const addIncoming = useCallback(
    async (sourceId: number) => {
      await addIncomingMutation.mutateAsync(sourceId);
    },
    [addIncomingMutation]
  );

  const remove = useCallback(
    async (id: number) => {
      setDeletingId(id);
      try {
        await removeMutation.mutateAsync(id);
      } finally {
        setDeletingId(null);
      }
    },
    [removeMutation]
  );

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    reloadBoardSettings();
  }, [reloadBoardSettings]);

  const contextValue = useMemo(
    () => ({
      results,
      isLoading,
      isPanelOpen,
      openPanel,
      closePanel,
      addOutgoing,
      addIncoming,
      remove,
      deletingId,
    }),
    [results, isLoading, isPanelOpen, openPanel, closePanel, addOutgoing, addIncoming, remove, deletingId]
  );

  return (
    <BoardSendToContext.Provider value={contextValue}>
      {children}
      {isPanelOpen && <BoardSendToPanel />}
    </BoardSendToContext.Provider>
  );
};
